import { getAccessToken, tryRefresh } from "./auth";
import type {
  Alert,
  County,
  GeocodedPlace,
  Location,
  Subscription,
  SubscriptionPayload,
  TerytBoundaryFeatureCollection,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function doFetch(path: string, init: RequestInit | undefined, token: string | null): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(path, { ...init, headers });
}

async function request<T>(path: string, init?: RequestInit, isRetry = false): Promise<T> {
  let response: Response;
  try {
    response = await doFetch(path, init, getAccessToken());
  } catch {
    throw new ApiError(`Brak połączenia z serwerem (${path})`, null);
  }

  if (response.status === 401 && !isRetry && getAccessToken() !== null) {
    await tryRefresh();
    return request<T>(path, init, true);
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (body.detail) detail = JSON.stringify(body.detail);
    } catch {
      // response body wasn't JSON — keep statusText
    }
    throw new ApiError(`${path} → ${response.status}: ${detail}`, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function getCounties(): Promise<County[]> {
  return request<County[]>("/counties");
}

export function getCountyBoundaries(): Promise<TerytBoundaryFeatureCollection> {
  return request<TerytBoundaryFeatureCollection>("/counties/boundaries");
}

export function getVoivodeshipBoundaries(): Promise<TerytBoundaryFeatureCollection> {
  return request<TerytBoundaryFeatureCollection>("/voivodeships/boundaries");
}

export function getAlerts(): Promise<Alert[]> {
  return request<Alert[]>("/alerts");
}

export function getSubscriptions(): Promise<Subscription[]> {
  return request<Subscription[]>("/subscriptions");
}

export function createSubscription(payload: SubscriptionPayload): Promise<Subscription> {
  return request<Subscription>("/subscriptions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateSubscription(id: number, payload: SubscriptionPayload): Promise<Subscription> {
  return request<Subscription>(`/subscriptions/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteSubscription(id: number): Promise<void> {
  return request<void>(`/subscriptions/${id}`, { method: "DELETE" });
}

export function searchPlaces(query: string): Promise<GeocodedPlace[]> {
  return request<GeocodedPlace[]>(`/locations/geo-search?q=${encodeURIComponent(query)}`);
}

export function createLocation(place: GeocodedPlace): Promise<Location> {
  return request<Location>("/locations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: place.name,
      latitude: place.latitude,
      longitude: place.longitude,
      place_type: place.place_type,
      voivodeship: place.voivodeship,
      county: place.county,
      municipality: place.municipality,
    }),
  });
}

export function getLocation(id: number): Promise<Location> {
  return request<Location>(`/locations/${id}`);
}
